import styles from './PrivacyPolicy.module.css';

const SECTIONS = [
    {
        id: 'overview',
        title: '1. Overview',
        content: [
            'Code Venture Labs ("we", "us", "our") is committed to protecting your personal data. This Privacy Policy explains what information we collect, how we use it, who we share it with, and what rights you have over it.',
            'This policy applies to all users of the Code Venture Labs platform, including visitors and registered users. By using the Platform you agree to the practices described here.',
        ],
    },
    {
        id: 'data-collected',
        title: '2. Data we collect',
        content: [
            'Account data: when you register, we collect your first name, last name, email address, and an encrypted password. If you add a phone number in your profile settings, we store that too.',
            'Usage data: we automatically collect information about how you interact with the Platform: pages visited, lessons completed, quiz results, time spent, and navigation paths. This is collected through our analytics providers (PostHog and Google Analytics) and helps us understand how you learn and improve the experience.',
            'Communications: if you contact our support team, we retain that correspondence to help resolve your issue and improve our service.',
            'Technical data: we collect your IP address, browser type, device type, and operating system for security, analytics, and compatibility purposes.',
        ],
    },
    {
        id: 'how-we-use',
        title: '3. How we use your data',
        content: [
            'To provide and improve the Platform: personalise your learning experience, track progress, recommend content, and fix bugs.',
            'To manage your account: authenticate you, send password resets, and respond to support requests.',
            'To understand and improve how the Platform is used: we use analytics (PostHog and Google Analytics) to measure engagement and improve our content and features.',
            'To communicate with you: send course updates, progress digests, and platform announcements. You can opt out of marketing emails at any time in your profile settings.',
            'To comply with legal obligations: retain records as required by applicable law and respond to lawful requests from authorities.',
        ],
    },
    {
        id: 'legal-basis',
        title: '4. Legal basis for processing',
        content: [
            'We process your data on the following legal bases under GDPR and applicable privacy law:',
            'Contract: processing necessary to deliver the service you signed up for (account management and course access).',
            'Legitimate interests: analytics and product-usage measurement (PostHog and Google Analytics), security monitoring, and product improvement, balanced against your privacy rights. You can object to this processing at any time.',
            'Consent: marketing emails. You may withdraw consent at any time.',
            'Legal obligation: compliance with applicable legal requirements and responding to lawful requests from authorities.',
        ],
    },
    {
        id: 'sharing',
        title: '5. Data sharing',
        content: [
            'We do not sell your personal data. We share it only with trusted third-party service providers who help us operate the Platform, under data processing agreements:',
            'AWS (Amazon Web Services): cloud infrastructure, hosting, and data storage. Amazon SES: transactional email delivery, such as password-reset messages. PostHog: product-usage analytics. Google Analytics: website and usage analytics.',
            'We may also disclose your data if required to do so by law or in response to valid legal process such as a court order or government request.',
        ],
    },
    {
        id: 'transfers',
        title: '6. International data transfers',
        content: [
            'Some of our service providers process data outside the European Economic Area (EEA). In particular, Google Analytics may transfer usage data to Google servers in the United States.',
            'Where data is transferred outside the EEA, we rely on appropriate safeguards such as the European Commission\'s Standard Contractual Clauses and, where applicable, the EU-US Data Privacy Framework, so that your data continues to receive an equivalent level of protection.',
            'We carry out analytics, including any such transfers, on the basis of our legitimate interest in understanding and improving the Platform. You can opt out through your browser settings.',
        ],
    },
    {
        id: 'retention',
        title: '7. Data retention',
        content: [
            'We retain your account data for as long as your account is active. If you delete your account, we delete your personal data within 30 days, except where we are required to retain certain information for legal compliance purposes.',
            'Anonymised, aggregated usage data may be retained indefinitely for analytics purposes.',
        ],
    },
    {
        id: 'cookies',
        title: '8. Cookies',
        content: [
            'We use strictly necessary cookies to keep you signed in and maintain your session. These are required for the Platform to function.',
            'We also use analytics cookies and similar technologies, set by PostHog and Google Analytics, to understand how the Platform is used and improve it. We show a cookie notice on your first visit; by continuing to use the Platform you agree to these cookies.',
            'You can opt out of analytics cookies through your browser settings or browser-level controls such as Do Not Track. Disabling strictly necessary cookies will prevent you from using the Platform.',
        ],
    },
    {
        id: 'security',
        title: '9. Security',
        content: [
            'We use industry-standard security measures including TLS encryption in transit, encryption at rest, and regular security reviews. Access to personal data is restricted to people who need it to perform their role.',
            'No method of transmission or storage is 100% secure. If you suspect your account has been compromised, contact us immediately at legal@codeventurelabs.dev.',
        ],
    },
    {
        id: 'your-rights',
        title: '10. Your rights',
        content: [
            'Depending on your location, you may have the following rights over your personal data:',
            'Access, request a copy of the data we hold about you. Correction, request that inaccurate data be corrected. Deletion, request that your data be deleted ("right to be forgotten"). Portability, receive your data in a structured, machine-readable format. Objection, object to processing based on legitimate interests. Restriction, request that we restrict processing of your data. Withdraw consent, where we rely on your consent (such as for marketing emails), withdraw it at any time.',
            'To exercise any of these rights, contact us at legal@codeventurelabs.dev. We will respond within 30 days. You also have the right to lodge a complaint with your local data protection authority.',
        ],
    },
    {
        id: 'children',
        title: '11. Children\'s privacy',
        content: [
            'The Platform is not directed at children under the age of 16. We do not knowingly collect personal data from children. If you believe a child has provided us with their data, please contact us and we will delete it promptly.',
        ],
    },
    {
        id: 'changes',
        title: '12. Changes to this policy',
        content: [
            'We may update this Privacy Policy from time to time. We will notify you of material changes via email or a prominent notice on the Platform at least 14 days before the change takes effect.',
            'Your continued use of the Platform after the effective date constitutes acceptance of the updated policy.',
        ],
    },
    {
        id: 'contact',
        title: '13. Contact',
        content: [
            'If you have any questions or concerns about this Privacy Policy or how we handle your data, please contact us:',
            'Email: legal@codeventurelabs.dev',
        ],
    },
];

export default function PrivacyPolicy() {
    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className={`${styles.page} container` }>

            {/* ── Hero ── */}
            <div className={styles.hero}>
                <div className={styles.heroInner}>
                    <span className={styles.eyebrow}>Legal</span>
                    <h1 className={styles.heroHeading}>Privacy Policy</h1>
                    <div className={styles.heroRule} />
                    <p className={styles.heroMeta}>
                        Last updated <strong>20 June 2026</strong> · Effective immediately
                    </p>
                    <p className={styles.heroLead}>
                        Your privacy matters to us. This policy explains exactly what data we collect,
                        why we collect it, and how you can control it.
                    </p>
                    <div className={styles.badges}>
                        <span className={styles.badge}>
                            <i className="fas fa-shield-halved" /> GDPR compliant
                        </span>
                        <span className={styles.badge}>
                            <i className="fas fa-ban" /> We never sell your data
                        </span>
                        <span className={styles.badge}>
                            <i className="fas fa-lock" /> Encrypted at rest & in transit
                        </span>
                    </div>
                </div>
            </div>

            <div className={styles.layout}>

                {/* ── Sidebar TOC ── */}
                <aside className={styles.toc}>
                    <p className={styles.tocLabel}>On this page</p>
                    <nav>
                        {SECTIONS.map((s) => (
                            <button
                                key={s.id}
                                className={styles.tocLink}
                                onClick={() => scrollTo(s.id)}
                            >
                                {s.title}
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* ── Content ── */}
                <article className={styles.content}>
                    {SECTIONS.map((s) => (
                        <section key={s.id} id={s.id} className={styles.section}>
                            <h2 className={styles.sectionTitle}>{s.title}</h2>
                            {s.content.map((para, i) => (
                                <p key={i} className={styles.para}>{para}</p>
                            ))}
                        </section>
                    ))}

                    <div className={styles.acceptance}>
                        <i className="fas fa-circle-check" />
                        <p>
                            If you have any questions about how we handle your data, reach out any time at{' '}
                            <a href="mailto:legal@codeventurelabs.dev" className={styles.link}>
                                legal@codeventurelabs.dev
                            </a>. We aim to respond within 2 business days.
                        </p>
                    </div>
                </article>

            </div>
        </div>
    );
}
